/**
 * Sprint 23.6 — AgentSettingsTab.
 *
 * The first chrome surface for ~7 AgentConfig fields that were previously
 * worker-only (Q11 / bash audit). Six stacked sections + an archive footer:
 *
 *   1. Persona        — the 5 Sprint 23.5 fields editable inline (bio, style,
 *                       voice_tone, motivations, specialties), each with a
 *                       sparkle that opens chat in composition scope.
 *   2. Instructions   — wraps the existing InstructionEditor + InheritanceStrip
 *                       components unchanged (Sprint ≤22 chrome).
 *   3. Access         — workspace_scope dropdown + table_scope multi-select +
 *                       equipment via AgentEquipmentEditor (Sprint 24 junction).
 *   4. Model          — model_policy dropdown + (when policy="fixed") a model
 *                       string field.
 *   5. Chat starters  — string[] editor (add/remove rows).
 *   6. Advanced       — collapsed <details> hiding max_steps + max_output_tokens.
 *   + Archive footer  — danger-coloured button + confirmation modal.
 *
 * Long-scroll over nested tabs by choice (Q11): each section is independently
 * editable, and 7 sections × ~120 px each scrolls in <1s. Owner-gating: every
 * editable affordance is wrapped in `canEdit` so a member viewing someone
 * else's agent sees the values but not the inputs (Q26 + Sprint 14c).
 */

import { useEffect, useState, type CSSProperties } from "react";
import { showToast, Card } from "../primitives";
import { useUpdateNode } from "../../../lib/hooks/use-kuvio";
import type {
  AgentConfig,
  LibraryNodeDetail,
  ModelPolicy,
  PersonaStyle,
} from "../../../lib/types";
import { InstructionEditor } from "../cockpit/InstructionEditor";
import { InheritanceStrip } from "../cockpit/InheritanceStrip";
import { InlinePersonaEdit } from "../persona/InlinePersonaEdit";
import { SparkleRegen } from "../persona/SparkleRegen";
import { AgentEquipmentEditor } from "./AgentEquipmentEditor";
import { ArchiveModal } from "../scope/ArchiveModal";
import { useLocation, useNavigate } from "react-router";

/**
 * Sprint 23.6.1 — deep-link section ids. The sidebar's "Edit in settings →"
 * affordance navigates to `#settings` (the settings tab) and then to one of
 * these subsection ids via the `settingsSection` location-state hint.
 */
export const SETTINGS_SECTION_IDS = {
  persona: "settings-persona",
  instructions: "settings-instructions",
  access: "settings-access",
  model: "settings-model",
  starters: "settings-starters",
} as const;
export type SettingsSectionKey = keyof typeof SETTINGS_SECTION_IDS;

const STYLE_OPTIONS: { value: PersonaStyle; label: string }[] = [
  { value: "matter-of-fact", label: "Matter-of-fact" },
  { value: "conversational", label: "Conversational" },
  { value: "playful", label: "Playful" },
  { value: "formal", label: "Formal" },
];

const POLICY_OPTIONS: { value: ModelPolicy; label: string; help: string }[] = [
  { value: "auto", label: "Auto", help: "Kuvio picks an appropriate model per task." },
  {
    value: "economy",
    label: "Economy",
    help: "Prefer cheaper models. Slower / less capable but lower spend.",
  },
  {
    value: "performance",
    label: "Performance",
    help: "Prefer top-tier models. Higher cost, faster + sharper answers.",
  },
  {
    value: "fixed",
    label: "Fixed",
    help: "Always use the model you specify below.",
  },
];

export interface AgentSettingsTabProps {
  node: LibraryNodeDetail;
  canEdit: boolean;
}

export function AgentSettingsTab({ node, canEdit }: AgentSettingsTabProps) {
  const updateNode = useUpdateNode();
  const navigate = useNavigate();
  const location = useLocation();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const config = (node.agent_config ?? null) as AgentConfig | null;

  // Sprint 23.6.1 — sidebar "Edit in settings →" passes the subsection key
  // through `location.state.settingsSection`; scroll it into view once mounted.
  useEffect(() => {
    const state = location.state as { settingsSection?: SettingsSectionKey } | null;
    const key = state?.settingsSection;
    if (!key) return;
    const id = SETTINGS_SECTION_IDS[key];
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.state, location.key]);

  async function savePersonaPatch(patch: Partial<AgentConfig>) {
    const next = {
      ...(node.agent_config ?? {}),
      ...patch,
    } as AgentConfig;
    try {
      await updateNode.mutateAsync({ id: node.id, agent_config: next });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "save failed";
      showToast(`Couldn't save: ${msg}`, "error", true);
      throw err;
    }
  }

  return (
    <div style={wrap}>
      {/* 1. Persona */}
      <Section
        id={SETTINGS_SECTION_IDS.persona}
        title="Persona"
        subtitle="How the AI introduces itself and what it cares about."
      >
        <SparkleRegen agentId={node.id} agentName={node.name} field="bio" style={config?.style ?? null}>
          {(open) => (
            <InlinePersonaEdit
              label="Bio"
              value={config?.bio ?? null}
              mode="textarea"
              placeholder="A few sentences about who this hire is."
              readOnly={!canEdit}
              onRegenerate={canEdit ? open : undefined}
              onSave={(next) =>
                savePersonaPatch({ bio: typeof next === "string" ? next : null })
              }
            />
          )}
        </SparkleRegen>
        <InlinePersonaEdit
          label="Style"
          value={config?.style ?? null}
          mode="select"
          options={STYLE_OPTIONS}
          placeholder="Pick a style"
          readOnly={!canEdit}
          onSave={(next) =>
            savePersonaPatch({
              style:
                typeof next === "string" && next.length > 0
                  ? (next as PersonaStyle)
                  : null,
            })
          }
        />
        <SparkleRegen agentId={node.id} agentName={node.name} field="voice_tone" style={config?.style ?? null}>
          {(open) => (
            <InlinePersonaEdit
              label="Voice & tone"
              value={config?.voice_tone ?? null}
              mode="textarea"
              placeholder="How they speak."
              readOnly={!canEdit}
              onRegenerate={canEdit ? open : undefined}
              onSave={(next) =>
                savePersonaPatch({
                  voice_tone: typeof next === "string" ? next : null,
                })
              }
            />
          )}
        </SparkleRegen>
        <SparkleRegen agentId={node.id} agentName={node.name} field="motivations" style={config?.style ?? null}>
          {(open) => (
            <InlinePersonaEdit
              label="Motivations"
              value={config?.motivations ?? null}
              mode="textarea"
              placeholder="What they care about. What they push back on."
              readOnly={!canEdit}
              onRegenerate={canEdit ? open : undefined}
              onSave={(next) =>
                savePersonaPatch({
                  motivations: typeof next === "string" ? next : null,
                })
              }
            />
          )}
        </SparkleRegen>
        <InlinePersonaEdit
          label="Specialties"
          value={config?.specialties ?? []}
          mode="tags"
          placeholder="What they're great at"
          readOnly={!canEdit}
          onSave={(next) =>
            savePersonaPatch({
              specialties: Array.isArray(next) && next.length > 0 ? next : null,
            })
          }
        />
      </Section>

      {/* 2. Instructions — absorbs the existing components unchanged. */}
      <Section
        id={SETTINGS_SECTION_IDS.instructions}
        title="Instructions"
        subtitle="System prompt fragments for this agent. Inherited ancestors stack above."
      >
        <InheritanceStrip node={node} />
        <InstructionEditor node={node} />
      </Section>

      {/* 3. Access */}
      <Section
        id={SETTINGS_SECTION_IDS.access}
        title="Access"
        subtitle="Where this agent can read and what it can run."
      >
        <SettingsField label="Workspace data" help="Visible to this agent.">
          <select
            disabled={!canEdit}
            value={config?.workspace_scope ?? "all"}
            onChange={(e) =>
              savePersonaPatch({
                workspace_scope: e.target.value as "all" | "children",
              })
            }
            style={inputStyle}
          >
            <option value="all">All workspace data</option>
            <option value="children">Children only</option>
          </select>
        </SettingsField>
        <SettingsField
          label="Table scope"
          help="Which entity tables the agent can read. Leave at All for full access."
        >
          <TableScopeSummary
            value={config?.table_scope ?? "all"}
            readOnly={!canEdit}
            onChange={(next) => savePersonaPatch({ table_scope: next })}
          />
        </SettingsField>
        <SettingsField
          label="Equipped with"
          help="Which connections this agent can use. Add or remove inline (Sprint 24 junction)."
        >
          <AgentEquipmentEditor nodeId={node.id} canEdit={canEdit} />
        </SettingsField>
      </Section>

      {/* 4. Model */}
      <Section
        id={SETTINGS_SECTION_IDS.model}
        title="Model"
        subtitle="Routing policy for LLM calls this agent makes."
      >
        <SettingsField label="Policy" help="">
          <select
            disabled={!canEdit}
            value={config?.model_policy ?? "auto"}
            onChange={(e) =>
              savePersonaPatch({
                model_policy: e.target.value as ModelPolicy,
              })
            }
            style={inputStyle}
          >
            {POLICY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} — {o.help}
              </option>
            ))}
          </select>
        </SettingsField>
        {(config?.model_policy ?? "auto") === "fixed" && (
          <SettingsField
            label="Model"
            help="Exact model identifier (e.g. claude-opus-4-8)."
          >
            <input
              type="text"
              disabled={!canEdit}
              defaultValue={config?.model ?? ""}
              placeholder="claude-opus-4-8"
              style={inputStyle}
              onBlur={(e) =>
                savePersonaPatch({ model: e.target.value || null })
              }
            />
          </SettingsField>
        )}
      </Section>

      {/* 5. Chat starters */}
      <Section
        id={SETTINGS_SECTION_IDS.starters}
        title="Chat starters"
        subtitle="Suggested prompts shown when starting a new chat with this agent."
      >
        <StartersEditor
          value={config?.starters ?? []}
          readOnly={!canEdit}
          onChange={(next) =>
            savePersonaPatch({
              starters: next.length === 0 ? null : next,
            })
          }
        />
      </Section>

      {/* 6. Advanced */}
      <details style={advancedDetails}>
        <summary style={advancedSummary}>Advanced</summary>
        <div style={advancedBody}>
          <SettingsField
            label="Max steps"
            help="Maximum tool-use loop iterations per agent run."
          >
            <input
              type="number"
              min={1}
              max={200}
              disabled={!canEdit}
              defaultValue={config?.max_steps ?? 50}
              style={inputStyle}
              onBlur={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n > 0) {
                  savePersonaPatch({ max_steps: n });
                }
              }}
            />
          </SettingsField>
          <SettingsField
            label="Max output tokens"
            help="Per-turn output cap. Leave blank for the model default."
          >
            <input
              type="number"
              min={1}
              max={64_000}
              disabled={!canEdit}
              defaultValue={config?.max_output_tokens ?? ""}
              placeholder="(model default)"
              style={inputStyle}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v.length === 0) {
                  savePersonaPatch({ max_output_tokens: null });
                  return;
                }
                const n = Number(v);
                if (Number.isFinite(n) && n > 0) {
                  savePersonaPatch({ max_output_tokens: n });
                }
              }}
            />
          </SettingsField>
        </div>
      </details>

      {/* Archive footer */}
      {canEdit && !node.is_root && (
        <Card style={archiveFooter}>
          <div style={archiveCol}>
            <div style={archiveTitle}>Archive {node.name}</div>
            <div style={archiveHelp}>
              Hides this agent and its content. You can restore from the workforce roster.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setArchiveOpen(true)}
            style={archiveBtn}
          >
            Archive {node.name}
          </button>
          <ArchiveModal
            open={archiveOpen}
            onClose={() => setArchiveOpen(false)}
            nodes={[node]}
            onArchived={() => {
              const parent = node._breadcrumb?.length
                ? node._breadcrumb[node._breadcrumb.length - 1]
                : null;
              navigate(parent ? `/agents/${parent.id}` : "/workforce");
            }}
          />
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — section/field wrappers and a couple of inline editors.
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  subtitle,
  children,
}: {
  id?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} style={section}>
      <header style={sectionHeader}>
        <div style={sectionTitle}>{title}</div>
        {subtitle && <div style={sectionSub}>{subtitle}</div>}
      </header>
      <div style={sectionBody}>{children}</div>
    </section>
  );
}

function SettingsField({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={fieldWrap}>
      <div style={fieldLabel}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
      {help && <div style={fieldHelp}>{help}</div>}
    </div>
  );
}

function TableScopeSummary({
  value,
  readOnly,
  onChange,
}: {
  value: "all" | string[];
  readOnly?: boolean;
  onChange: (next: "all" | string[]) => void;
}) {
  // v1: All / Restricted toggle with a comma-separated input for slugs. A
  // proper entity-type multi-select would mean threading useListEntityTypes
  // through here — kept out of scope to keep the section terse. The CSV input
  // round-trips cleanly with the worker (which accepts string[]).
  const isAll = value === "all";
  return (
    <div>
      <select
        disabled={readOnly}
        value={isAll ? "all" : "restricted"}
        onChange={(e) =>
          onChange(e.target.value === "all" ? "all" : Array.isArray(value) ? value : [])
        }
        style={inputStyle}
      >
        <option value="all">All tables</option>
        <option value="restricted">Restricted</option>
      </select>
      {!isAll && (
        <input
          type="text"
          disabled={readOnly}
          defaultValue={(value as string[]).join(", ")}
          placeholder="comma-separated entity slugs (e.g. contact, account)"
          style={{ ...inputStyle, marginTop: 4 }}
          onBlur={(e) => {
            const parts = e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
            onChange(parts);
          }}
        />
      )}
    </div>
  );
}

function StartersEditor({
  value,
  readOnly,
  onChange,
}: {
  value: string[];
  readOnly?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  function add(raw: string) {
    const next = raw.trim();
    if (!next || value.includes(next)) return;
    onChange([...value, next]);
    setDraft("");
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {value.map((s, i) => (
        <div key={`${i}:${s}`} style={starterRow}>
          <span style={starterText}>{s}</span>
          {!readOnly && (
            <button
              type="button"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              style={starterRemove}
              aria-label={`Remove starter ${s}`}
            >
              <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
            }
          }}
          onBlur={() => add(draft)}
          placeholder="Add a chat starter — Enter to save"
          style={inputStyle}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  padding: 0,
};

const section: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  paddingBottom: 18,
  borderBottom: "0.5px solid var(--color-border-tertiary)",
};

const sectionHeader: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const sectionTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const sectionSub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};

const sectionBody: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  paddingTop: 4,
};

const fieldWrap: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: 10,
  alignItems: "flex-start",
};

const fieldLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  paddingTop: 8,
};

const fieldHelp: CSSProperties = {
  gridColumn: "2",
  fontSize: 11,
  color: "var(--color-text-tertiary)",
};

const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "6px 8px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};

const advancedDetails: CSSProperties = {
  borderBottom: "0.5px solid var(--color-border-secondary)",
  paddingBottom: 14,
};

const advancedSummary: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  padding: "6px 0",
};

const advancedBody: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  paddingTop: 12,
};

const starterRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-secondary)",
};

const starterText: CSSProperties = {
  flex: 1,
  fontSize: 13,
  color: "var(--color-text-primary)",
};

const starterRemove: CSSProperties = {
  display: "inline-flex",
  width: 22,
  height: 22,
  border: "none",
  background: "transparent",
  color: "var(--color-text-tertiary)",
  cursor: "pointer",
  borderRadius: 4,
};

const archiveFooter: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  background: "color-mix(in srgb, var(--color-text-coral) 4%, transparent)",
  border: "0.5px solid color-mix(in srgb, var(--color-text-coral) 24%, var(--color-border-secondary))",
};

const archiveCol: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const archiveTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const archiveHelp: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
};

const archiveBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "8px 14px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-coral)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontFamily: "var(--font-sans)",
};
